import * as React from 'react';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  DragOverlay,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  HStack,
  TextInput,
  VStack,
  Text,
  View,
  RowItem,
  RowItemBody,
  RowItemActions,
  RowItemAction,
  DragItemWrapper,
  DragItemHandle,
  AddIcon,
  DeleteIcon,
  ElementComponentIcon,
  Column,
  ChevronSmallDownIcon,
  ChevronSmallRightIcon,
  EmptyState,
  EmptyStateBody,
  EmptyComponentGlobalStyle64Icon,
  SearchDefaultIcon,
  Tooltip,
  HelpCircleIcon,
  ComponentGlobalStyleIcon,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {useTranslation} from '@packages/systems/designer-translations';
import {slugify} from '@packages/utilities/string-utils';
import {useGlobalComponentsManager} from '../hooks/useGlobalComponentsManager';
import type {ComponentItem} from '../types';

const StyledContainer = emotionStyled(HStack)({
  height: '100%',
  borderTop: '$border1',
  overflow: 'hidden',
  gap: 0,
});

const StyledColumn = emotionStyled(Column)(
  {
    display: 'flex',
    flex: 1,
    width: '50%',
    height: '100%',
    padding: '0 $3',
    flexDirection: 'column',
  },
  {
    variants: {
      hasBorder: {
        true: {
          borderLeft: '$border1',
        },
      },
    },
  }
);

const StyledColumnHeader = emotionStyled(HStack)({
  height: '$12',
});

const ScrollableList = emotionStyled(View)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  paddingBottom: '$2',
});

const StyledRowItemBody = emotionStyled(RowItemBody)(
  {},
  {
    variants: {
      isGrouped: {
        true: {
          paddingLeft: '$4',
        },
      },
    },
  }
);

const StyledDragItemWrapper = emotionStyled(DragItemWrapper)(
  {},
  {variants: {isDragging: {true: {pointerEvents: 'none'}}}}
);

function formatComponentDisplayName(component: ComponentItem): string {
  return `${component.group ? component.group + ' / ' : ''}${component.name}`;
}

type DraggableRowProps = {
  component: ComponentItem;
  onRemove: (id: string) => void;
};

const DraggableRow: React.FC<DraggableRowProps> = ({component, onRemove}) => {
  const t = useTranslation();
  const {isDragging, attributes, listeners, setNodeRef, setActivatorNodeRef} =
    useSortable({
      id: component.id,
    });

  const dragProps = {
    ...attributes,
    ...listeners,
  };

  const displayName = formatComponentDisplayName(component);

  return (
    <StyledDragItemWrapper isDragging={isDragging} ref={setNodeRef}>
      <RowItem
        isRounded
        prefix={ComponentGlobalStyleIcon}
        data-automation-id={`global-component-settings-draggable-${slugify(component.name)}`}
      >
        <DragItemHandle
          {...dragProps}
          ref={setActivatorNodeRef}
          data-automation-id="global-component-settings-drag-handle"
        />
        <RowItemBody>{displayName}</RowItemBody>
        <RowItemActions>
          <RowItemAction
            data-automation-id="global-component-settings-remove-item"
            Icon={DeleteIcon}
            label={t(
              'designer.components.global-component-settings.panel.remove'
            )}
            onClick={() => onRemove(component.id)}
          />
        </RowItemActions>
      </RowItem>
    </StyledDragItemWrapper>
  );
};

type DragOverlayRowProps = {
  component: ComponentItem;
};

const DragOverlayRow: React.FC<DragOverlayRowProps> = ({component}) => {
  const displayName = formatComponentDisplayName(component);

  return (
    <DragItemWrapper>
      <RowItem isDraggedItem isRounded prefix={ComponentGlobalStyleIcon}>
        <RowItemBody>{displayName}</RowItemBody>
      </RowItem>
    </DragItemWrapper>
  );
};

type AvailableComponentRowProps = {
  component: ComponentItem;
  onAdd: (id: string) => void;
  isGrouped: boolean;
};

const AvailableComponentRow: React.FC<AvailableComponentRowProps> = ({
  component,
  onAdd,
  isGrouped,
}) => {
  const t = useTranslation();
  return (
    <RowItem
      isRounded
      prefix={ElementComponentIcon}
      data-automation-id={`global-component-settings-item-${slugify(component.name)}`}
    >
      <StyledRowItemBody isGrouped={isGrouped}>
        {component.name}
      </StyledRowItemBody>
      <RowItemActions>
        <RowItemAction
          data-automation-id="global-component-settings-add-item"
          Icon={AddIcon}
          label={t('designer.components.global-component-settings.panel.add')}
          onClick={() => onAdd(component.id)}
        />
      </RowItemActions>
    </RowItem>
  );
};

type ComponentGroupHeaderProps = {
  groupName: string;
  isExpanded: boolean;
  onToggle: () => void;
};

const ComponentGroupHeader: React.FC<ComponentGroupHeaderProps> = ({
  groupName,
  isExpanded,
  onToggle,
}) => {
  const ChevronIcon = isExpanded ? ChevronSmallDownIcon : ChevronSmallRightIcon;

  return (
    <CollapsibleTrigger onClick={onToggle}>
      <HStack paddingX="$2" paddingY="$1" cursor="pointer">
        <Text font="helpText" colorVariant="secondary">
          {groupName}
        </Text>
        <ChevronIcon colorVariant="secondary" />
      </HStack>
    </CollapsibleTrigger>
  );
};

export const GlobalComponentsPanelBody = () => {
  const t = useTranslation();
  const {
    selectedIds,
    selectedComponents,
    availableComponents,
    groupedAvailableComponents,
    searchQuery,
    setSearchQuery,
    activeComponent,
    collapsedGroups,
    handleToggleGroup,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleAdd,
    handleRemove,
  } = useGlobalComponentsManager();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <StyledContainer data-automation-id="global-component-settings-panel">
      <StyledColumn>
        <StyledColumnHeader>
          <TextInput
            data-automation-id="global-component-settings-input"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            placeholder={t(
              'designer.components.global-component-settings.panel.input.placeholder'
            )}
            prefix={SearchDefaultIcon}
            value={searchQuery}
          />
        </StyledColumnHeader>

        <ScrollableList data-automation-id="global-component-settings-available-list">
          {availableComponents.length === 0 ? (
            <Text colorVariant="secondary" padding="$2">
              {searchQuery
                ? t(
                    'designer.components.global-component-settings.panel.no-match'
                  )
                : t(
                    'designer.components.global-component-settings.panel.all-added'
                  )}
            </Text>
          ) : (
            <VStack spacing={0}>
              {[...groupedAvailableComponents.entries()].map(
                ([groupName, components]) => {
                  const isExpanded = !collapsedGroups.has(groupName);
                  const isGrouped = groupName !== '';

                  return (
                    <React.Fragment key={`group-${groupName}`}>
                      <Collapsible defaultIsOpen isOpen={isExpanded}>
                        {isGrouped && (
                          <ComponentGroupHeader
                            groupName={groupName}
                            isExpanded={isExpanded}
                            onToggle={() => handleToggleGroup(groupName)}
                          />
                        )}
                        <CollapsibleContent>
                          {components.map((component) => (
                            <AvailableComponentRow
                              component={component}
                              key={'available-' + component.id}
                              onAdd={handleAdd}
                              isGrouped={isGrouped}
                            />
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    </React.Fragment>
                  );
                }
              )}
            </VStack>
          )}
        </ScrollableList>
      </StyledColumn>

      <StyledColumn hasBorder>
        <StyledColumnHeader data-automation-id="global-component-settings-active-list">
          <Text weight="medium">
            {t(
              'designer.components.global-component-settings.panel.active.title'
            )}
          </Text>
          {selectedComponents.length > 0 && (
            <Tooltip
              label={t(
                'designer.components.global-component-settings.panel.empty-state.description'
              )}
            >
              <HelpCircleIcon aria-label="More info" colorVariant="dimmed" />
            </Tooltip>
          )}
        </StyledColumnHeader>

        <ScrollableList>
          {selectedComponents.length === 0 ? (
            <EmptyState
              title={t(
                'designer.components.global-component-settings.panel.empty-state.title'
              )}
              icon={EmptyComponentGlobalStyle64Icon}
            >
              <EmptyStateBody>
                <Text
                  colorVariant="secondary"
                  data-automation-id="global-component-settings-no-active-components-text"
                >
                  {t(
                    'designer.components.global-component-settings.panel.empty-state.description'
                  )}
                </Text>
              </EmptyStateBody>
            </EmptyState>
          ) : (
            <DndContext
              collisionDetection={closestCenter}
              onDragCancel={handleDragCancel}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              sensors={sensors}
            >
              <SortableContext
                items={selectedIds}
                strategy={verticalListSortingStrategy}
              >
                <VStack spacing={0}>
                  {selectedComponents.map((component) => (
                    <DraggableRow
                      component={component}
                      key={component.id}
                      onRemove={handleRemove}
                    />
                  ))}
                </VStack>
              </SortableContext>
              <DragOverlay>
                {activeComponent ? (
                  <DragOverlayRow component={activeComponent} />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </ScrollableList>
      </StyledColumn>
    </StyledContainer>
  );
};
